import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { allArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = useMemo(() => allArticles.find((a) => a.id === Number(id)), [id]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-unknow-line text-2xl"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-950 mb-2">文章未找到</h2>
            <p className="text-foreground-600 mb-6">这篇文章可能已被删除或链接失效</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium text-sm cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = allArticles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          author: { '@type': 'Person', name: article.author },
          datePublished: article.date,
          image: article.image,
          publisher: {
            '@type': 'Organization',
            name: '男同网',
            url: 'https://www.nantongwang.com/',
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://www.nantongwang.com/article/${article.id}`,
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.nantongwang.com/' },
            { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://www.nantongwang.com/category/${article.category}` },
            { '@type': 'ListItem', position: 3, name: article.title },
          ],
        }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20">
        <article>
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
              <nav className="flex items-center flex-wrap gap-2 text-sm text-foreground-500 mb-4">
                <Link to="/" className="hover:text-foreground-800 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link to={`/category/${article.category}`} className="hover:text-foreground-800 transition-colors cursor-pointer">
                  {article.categoryName}
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
              </nav>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight font-heading">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500 mb-4">
                <span className="font-medium text-foreground-700">{article.author}</span>
                <span>·</span>
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.readTime}</span>
              </div>

              <div className="flex flex-wrap gap-2">
                {article.tags?.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
            <div className="relative h-64 md:h-96 rounded-2xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <div className="mt-16 pt-10 border-t border-background-200/70">
                <h3 className="text-lg font-semibold text-foreground-950 mb-6 font-heading">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.id}`}
                      className="group rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={related.image}
                          alt={related.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors font-heading">
                          {related.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-2">{related.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}